import './style.css'

import { useEffect } from 'react'
import Odometer from 'react-odometerjs'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { useOnboardingProgressContext } from 'shared/components/Context/OnboardingProgressContext'
import { TextV2 } from 'shared/components/Text/TextV2'
import { useOnboardingChecklist } from 'shared/hooks/query/useOnboardingChecklist'
import { shouldShowOnboardingChecklist } from 'shared/models/onboarding'
import { TYPOGRAPHY } from 'shared/theme/typographyV2'

import { useHasL2oLinearOnboardingFlag } from '../../hooks/useHasFeatureFlag'
import { useProductSubscription } from '../../hooks/useProductSubscription'
import { getOnboardingProgressPercentage } from '../../models/linearOnboarding'
import { currentUserSelector, isVendorUserSelector } from '../../redux/selectors/users'
import { ONBOARDING_APPLICATION_STATUS_PATH } from '../../Routes'

interface ProgressContainerProps {
  children: React.ReactNode
}

interface ProgressBarProps {
  percentage: number
}

const ProgressContainer = ({ children }: ProgressContainerProps) => {
  return <div className="bg-light-gray4 relative mt-6 h-1 w-full overflow-hidden">{children}</div>
}

const ProgressBar = ({ percentage }: ProgressBarProps) => {
  return (
    <div
      className="bg-cyan-d1 absolute top-0 left-0 size-full transition-transform duration-1000 ease-in-out will-change-transform"
      style={{ transform: `translateX(-${100 - percentage}%)` }}
    />
  )
}

const DISABLED_PATHS = [ONBOARDING_APPLICATION_STATUS_PATH]

export const OnboardingProgress = () => {
  const { isVisible, setIsVisible, task, step } = useOnboardingProgressContext()
  const currentUser = useSelector(currentUserSelector)
  const isVendorUser = useSelector(isVendorUserSelector)
  const location = useLocation()
  const { isL2o } = useProductSubscription()
  const hasL2oLinearOnboardingFlag = useHasL2oLinearOnboardingFlag()
  const { checklist, roadChecklist } = useOnboardingChecklist(currentUser.id, {
    enabled: false,
  })
  const showRoadChecklist = shouldShowOnboardingChecklist(checklist, roadChecklist)
  const percentage = getOnboardingProgressPercentage(
    showRoadChecklist ? roadChecklist : checklist,
    task,
    step,
    showRoadChecklist ? 50 : 5,
    showRoadChecklist ? 100 : 50
  )

  const isProgressBarEnabled = (!isL2o || hasL2oLinearOnboardingFlag) && currentUser && !isVendorUser

  useEffect(() => {
    if (!isProgressBarEnabled) return
    const showOnboardingProgress =
      location.pathname.includes('onboarding') && !DISABLED_PATHS.includes(location.pathname)
    setIsVisible(showOnboardingProgress)
  }, [location, setIsVisible, isProgressBarEnabled])

  if (!isProgressBarEnabled || !isVisible) return null

  return (
    <div className="max-w-wide-wrap mx-auto my-0 w-full">
      <ProgressContainer>
        <ProgressBar percentage={percentage} />
      </ProgressContainer>
      <TextV2 type={TYPOGRAPHY.header4} className="text-dark-gray mt-2 text-right">
        <Odometer value={percentage} />% completed
      </TextV2>
    </div>
  )
}
